// src/pages/settings/settings/CompanyHours.tsx
import React, { useState, useEffect } from "react";
import { Button, TimePicker, Row, Col, Typography } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

// API
import { updateCompany } from "../../../api/admin/companyApi";
import { Company } from "../../../types/admin/CompanyTypes";

// Hooks
import { useNotifications } from "../../../context/NotificationContext";
import { SectionCard } from "../../../components/shared";

const { Text } = Typography;

interface DaySchedule {
	start: Date | null;
	end: Date | null;
}

const defaultSchedule: { [day: string]: DaySchedule } = {
	Monday: { start: new Date(1970, 0, 1, 9, 0), end: new Date(1970, 0, 1, 17, 0) },
	Tuesday: { start: new Date(1970, 0, 1, 9, 0), end: new Date(1970, 0, 1, 17, 0) },
	Wednesday: { start: new Date(1970, 0, 1, 9, 0), end: new Date(1970, 0, 1, 17, 0) },
	Thursday: { start: new Date(1970, 0, 1, 9, 0), end: new Date(1970, 0, 1, 17, 0) },
	Friday: { start: new Date(1970, 0, 1, 9, 0), end: new Date(1970, 0, 1, 17, 0) },
	Saturday: { start: null, end: null },
	Sunday: { start: null, end: null },
};

interface CompanyHoursProps {
	company: Company;
	onCompanyUpdated: (company: Company) => void;
}

const CompanyHours: React.FC<CompanyHoursProps> = ({ company, onCompanyUpdated }) => {
	const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
	const [schedule, setSchedule] = useState<{ [day: string]: DaySchedule }>(defaultSchedule);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const { showSuccess, showError } = useNotifications();

	useEffect(() => {
		if (company.schedule) {
			try {
				const scheduleFromDb: { [day: string]: { start: string | null; end: string | null } } = JSON.parse(company.schedule);
				const parsedSchedule: { [day: string]: DaySchedule } = {};
				for (const day in scheduleFromDb) {
					parsedSchedule[day] = {
						start: scheduleFromDb[day].start ? new Date(scheduleFromDb[day].start as string) : null,
						end: scheduleFromDb[day].end ? new Date(scheduleFromDb[day].end as string) : null,
					};
				}
				setSchedule(parsedSchedule);
			} catch (error) {
				console.error("Error parsing schedule:", error);
				setSchedule(defaultSchedule);
			}
		} else {
			setSchedule(defaultSchedule);
		}
	}, [company]);

	const handleScheduleChange = (day: string, field: "start" | "end", value: dayjs.Dayjs | null) => {
		setSchedule((prev) => ({
			...prev,
			[day]: {
				...prev[day],
				[field]: value ? value.toDate() : null,
			},
		}));
	};

	const handleSaveSchedule = async () => {
		setIsSaving(true);
		try {
			const updatedCompany = {
				...company,
				schedule: JSON.stringify(schedule),
			};

			await updateCompany(company.id, updatedCompany);
			onCompanyUpdated(updatedCompany);
			showSuccess("Company hours updated successfully");
		} catch (error) {
			console.error("Error saving schedule:", error);
			showError("Failed to save company hours");
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<SectionCard
			title="Operating Hours"
			extra={
				<Button type="primary" icon={<SaveOutlined />} loading={isSaving} onClick={handleSaveSchedule}>
					Save Hours
				</Button>
			}
		>
			<div>
				{daysOfWeek.map((day) => (
					<Row key={day} gutter={16} style={{ marginBottom: 12, alignItems: "center" }}>
						<Col span={6}>
							<Text strong style={{ fontSize: 13 }}>
								{day}
							</Text>
						</Col>
						<Col span={9}>
							<TimePicker value={schedule[day]?.start ? dayjs(schedule[day].start) : null} onChange={(time) => handleScheduleChange(day, "start", time)} format="HH:mm" placeholder="Start Time" style={{ width: "100%" }} />
						</Col>
						<Col span={9}>
							<TimePicker value={schedule[day]?.end ? dayjs(schedule[day].end) : null} onChange={(time) => handleScheduleChange(day, "end", time)} format="HH:mm" placeholder="End Time" style={{ width: "100%" }} />
						</Col>
					</Row>
				))}
			</div>
		</SectionCard>
	);
};

export default CompanyHours;
